import { useState } from 'react';
import type { Order } from '../types';
import { OrderRow } from './OrderRow';

interface OrdersTableProps {
  orders: Order[];
  isLoading: boolean;
}

type FilterStatus = 'all' | 'pending' | 'action_required' | 'resolved';

function isActionRequired(order: Order): boolean {
  return order.status === 'pending' && order.reason !== 'payment pending';
}

export function OrdersTable({ orders, isLoading }: OrdersTableProps) {
  const [filter, setFilter] = useState<FilterStatus>('all');
  const [search, setSearch] = useState('');

  const filteredOrders = orders.filter((order) => {
    // Filter by status
    if (filter === 'action_required' && !isActionRequired(order)) {
      return false;
    } else if (filter === 'pending' && !(order.status === 'pending' && order.reason === 'payment pending')) {
      return false;
    } else if (filter === 'resolved' && order.status !== 'resolved') {
      return false;
    }

    // Filter by search
    if (search) {
      const searchLower = search.toLowerCase();
      return (
        order.orderId.toLowerCase().includes(searchLower) ||
        (order.reason && order.reason.toLowerCase().includes(searchLower)) ||
        (order.notes && order.notes.toLowerCase().includes(searchLower))
      );
    }

    return true;
  });

  const actionRequiredCount = orders.filter(isActionRequired).length;
  const pendingCount = orders.filter((o) => o.status === 'pending' && o.reason === 'payment pending').length;
  const resolvedCount = orders.filter((o) => o.status === 'resolved').length;

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="animate-pulse p-4 space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="flex space-x-4">
              <div className="h-4 bg-gray-200 rounded w-24" />
              <div className="h-4 bg-gray-200 rounded w-32" />
              <div className="h-4 bg-gray-200 rounded w-16" />
              <div className="h-4 bg-gray-200 rounded w-20" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      {/* Filters */}
      <div className="p-4 border-b border-gray-200 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex gap-2">
          <button
            onClick={() => setFilter('all')}
            className={`px-3 py-1.5 text-sm font-medium rounded-md ${
              filter === 'all'
                ? 'bg-gray-900 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            All ({orders.length})
          </button>
          <button
            onClick={() => setFilter('action_required')}
            className={`px-3 py-1.5 text-sm font-medium rounded-md ${
              filter === 'action_required'
                ? 'bg-red-500 text-white'
                : 'bg-red-100 text-red-700 hover:bg-red-200'
            }`}
          >
            Action Required ({actionRequiredCount})
          </button>
          <button
            onClick={() => setFilter('pending')}
            className={`px-3 py-1.5 text-sm font-medium rounded-md ${
              filter === 'pending'
                ? 'bg-yellow-500 text-white'
                : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
            }`}
          >
            Pending ({pendingCount})
          </button>
          <button
            onClick={() => setFilter('resolved')}
            className={`px-3 py-1.5 text-sm font-medium rounded-md ${
              filter === 'resolved'
                ? 'bg-green-500 text-white'
                : 'bg-green-100 text-green-700 hover:bg-green-200'
            }`}
          >
            Resolved ({resolvedCount})
          </button>
        </div>

        <div className="w-full sm:w-auto">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search orders..."
            className="w-full sm:w-64 px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10">
                &nbsp;
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Order ID
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Reason
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Resolved By
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredOrders.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                  {search || filter !== 'all'
                    ? 'No orders match your filters'
                    : 'No orders in this batch'}
                </td>
              </tr>
            ) : (
              filteredOrders.map((order) => (
                <OrderRow key={order.orderId} order={order} />
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
